import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  describeCounts,
  describeOutcome,
  fetchMigrationPreview,
  runMigrations,
  type MigrationPreview,
  type PreviewScript,
} from '../../utils/runMigrations';
import './RunMigrationsDialog.css';

type Get = (url: string) => Promise<{ status: number; data: unknown }>;

interface RunMigrationsDialogProps {
  open: boolean;
  onClose: () => void;
  /** apiClient.get, so tests can answer without an API. */
  get: Get;
}

const REASON_TEXT: Record<string, string> = {
  New: 'not run yet',
  Changed: 'changed since it ran',
  RetryFailed: 'failed last time',
  Forced: 're-apply',
  Repeatable: 'clean-up sweep',
  NoJournal: 'always re-applied',
};

const errorMessage = (err: unknown): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Unknown error';
};

/**
 * PSI's Run Migrations: the Preview first, then Run for the scripts ticked.
 *
 * Preview (GET /api/Migration/Preview) reads the connection's journal and
 * counts what each pending script would change, without changing anything;
 * Run stays disabled until it has answered. Run sends only= with the ticked
 * scripts, so nothing that wasn't on screen can run. "Re-apply all scripts"
 * is the old forced run of everything (force=true).
 *
 * Desktop connections never reach this dialog (the connection picker only
 * lists web connections); the API refuses a full run there anyway, and PSI
 * runs named scripts for them with the API directly (docs/web-migrations.md).
 */
export default function RunMigrationsDialog({ open, onClose, get }: RunMigrationsDialogProps) {
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState<MigrationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ text: string; ok: boolean } | null>(null);
  // After a run the preview no longer describes the database: Run waits for a fresh one.
  const [stale, setStale] = useState(false);

  const loadPreview = useCallback(async (reapplyAll: boolean) => {
    setLoading(true);
    setLoadError(null);
    setPreview(null);
    setSelected(new Set());
    setStale(false);
    try {
      const p = await fetchMigrationPreview(get, { force: reapplyAll });
      setPreview(p);
      setSelected(new Set(p.scripts.filter((s) => s.wouldRun).map((s) => s.script)));
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    if (open) {
      setResult(null);
      void loadPreview(force);
    }
    // Only when the dialog opens; the force toggle reloads it itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const candidates = useMemo(() => (preview?.scripts ?? []).filter((s) => s.wouldRun), [preview]);
  const skipped = useMemo(() => (preview?.scripts ?? []).filter((s) => s.skipped), [preview]);
  const chosen = candidates.filter((s) => selected.has(s.script));
  const canRun = !!preview && !loading && !running && !stale && !preview.refused && chosen.length > 0;

  const toggle = (script: PreviewScript) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(script.script)) next.delete(script.script);
      else next.add(script.script);
      return next;
    });
  };

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    setResult(null);
    try {
      // Starts the run, then polls Status: a run outlasts Front Door's 30 s limit.
      const outcome = await runMigrations(get, { filter: { only: chosen.map((s) => s.script), force } });
      setResult(describeOutcome(outcome));
    } catch (err) {
      setResult({ text: `Migration failed: ${errorMessage(err)}`, ok: false });
    } finally {
      setRunning(false);
      setStale(true);
    }
  };

  const dataSteps = chosen.filter((s) => s.types.includes('data')).length;
  const settingSteps = chosen.filter((s) => s.types.includes('setting')).length;

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="run-migrations-title">
      <DialogTitle id="run-migrations-title" className="rmd-title">
        <i className="bi bi-database-gear" aria-hidden="true" /> Run Migrations
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <div className="rmd-status" role="status">
            <CircularProgress size={18} /> Checking what this database still needs...
          </div>
        )}
        {loadError && <div className="rmd-error" role="alert">Preview failed: {loadError}</div>}

        {preview && (
          <>
            <p className="rmd-summary">
              Connection {preview.connectionId} ({preview.connectionKind}) · build {preview.build}
              {' · '}
              {candidates.length === 0
                ? 'nothing to run'
                : `${chosen.length} of ${candidates.length} script(s) selected, ${dataSteps} change existing rows, ${settingSteps} change settings`}
            </p>
            {preview.refused && <div className="rmd-error" role="alert">{preview.refused}</div>}
            {preview.runInProgress && (
              <div className="rmd-warning" role="status">An update of this database is already running (run {preview.runInProgress}).</div>
            )}
            <ul className="rmd-databases">
              {preview.databases.map((d) => (
                <li key={d.lane}>
                  {d.lane === 'Server' ? 'PM_Server' : 'Facility database'}:{' '}
                  {d.reachable
                    ? `structure ${d.dbVersion ?? '?'}, ${d.journalExists ? 'web journal present' : 'no web journal yet'}${d.upToDate ? ', up to date' : ''}`
                    : `not reachable (${d.error ?? 'unknown'})`}
                </li>
              ))}
            </ul>

            {candidates.length > 0 && (
              <FormControlLabel
                className="rmd-all"
                control={
                  <Checkbox
                    size="small"
                    checked={chosen.length === candidates.length}
                    indeterminate={chosen.length > 0 && chosen.length < candidates.length}
                    onChange={(e) => setSelected(new Set(e.target.checked ? candidates.map((s) => s.script) : []))}
                    disabled={running}
                  />
                }
                label="All scripts listed"
              />
            )}

            <ul className="rmd-scripts" aria-label="Scripts that would run">
              {candidates.map((s) => (
                <li key={`${s.lane}/${s.script}`} className="rmd-script">
                  <FormControlLabel
                    control={<Checkbox size="small" checked={selected.has(s.script)} onChange={() => toggle(s)} disabled={running} />}
                    label={
                      <span className="rmd-script-name">
                        {s.script}
                        <span className="rmd-lane">{s.lane === 'Server' ? 'PM_Server' : s.lane}</span>
                        {s.types.map((t) => (
                          <span key={t} className={`rmd-type rmd-type-${t}`}>{t}</span>
                        ))}
                        <span className="rmd-reason">{REASON_TEXT[s.reason ?? ''] ?? s.reason}</span>
                      </span>
                    }
                  />
                  <div className="rmd-detail">
                    <div>{s.summary}</div>
                    {s.objects && s.objects.total > 0 && (
                      <div>
                        {s.objects.missing === 0
                          ? `Its ${s.objects.total} table(s), column(s) and index(es) are already there.`
                          : `${s.objects.missing} of its ${s.objects.total} table(s), column(s) and index(es) are missing.`}
                      </div>
                    )}
                    {describeCounts(s).map((line) => (
                      <div key={line} className="rmd-count">{line}</div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>

            {skipped.length > 0 && (
              <div className="rmd-skipped">
                Left out: {skipped.map((s) => `${s.script} (${s.skipped})`).join('; ')}
              </div>
            )}

            <FormControlLabel
              className="rmd-force"
              control={
                <Checkbox
                  size="small"
                  checked={force}
                  disabled={loading || running}
                  onChange={(e) => {
                    setForce(e.target.checked);
                    void loadPreview(e.target.checked);
                  }}
                />
              }
              label="Re-apply all scripts, including ones already applied"
            />
          </>
        )}

        {running && (
          <div className="rmd-status" role="status">
            <CircularProgress size={18} /> Running {chosen.length} script(s)...
          </div>
        )}
        {result && (
          <div className={result.ok ? 'rmd-ok' : 'rmd-error'} role={result.ok ? 'status' : 'alert'}>
            {result.text}
            {stale && ' Preview again to see what is left.'}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void loadPreview(force)} disabled={loading || running} size="small">
          Preview again
        </Button>
        <Button onClick={onClose} disabled={running} size="small">
          Cancel
        </Button>
        <Button onClick={() => void run()} disabled={!canRun} variant="contained" size="small">
          {running ? 'Running...' : `Run ${chosen.length} script(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
